import { Caption1, Tooltip, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { useId, useRef, useState, type PointerEvent } from "react";
import { useDataTableStyles } from "@/components/data-table";
import { EmphasisValue } from "@/components/ui-typography";
import { SHARE_HOLE, SHARE_RING, shareCenterPx, shareCenterScale, type ShareCenterPx } from "@/components/visual/share-center";

/**
 * 차트2의 큰 표식. 글이 본문이고, 색은 지연·막힘에만 쓴다.
 */

const SLICE = [
  tokens.colorNeutralForeground2,
  tokens.colorNeutralForeground3,
  tokens.colorNeutralStrokeAccessible,
  tokens.colorNeutralForeground4,
  tokens.colorNeutralStroke1,
];

const LEVEL = [
  tokens.colorNeutralBackground4,
  tokens.colorNeutralStroke2,
  tokens.colorNeutralStroke1,
  tokens.colorNeutralForeground4,
  tokens.colorNeutralForeground3,
];

/** 구멍 안 합계. 20px가 넘치면 한 단계씩 줄인다. 11px는 캡션 크기. */
const CENTER_TYPE: Record<ShareCenterPx, { fontSize: string; lineHeight: string }> = {
  20: { fontSize: tokens.fontSizeBase500, lineHeight: tokens.lineHeightBase500 },
  16: { fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400 },
  13: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
  12: { fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 },
  11: { fontSize: "11px", lineHeight: "14px" },
};

export interface SharePart {
  key: string;
  label: string;
}

export interface ShareRow {
  key: string;
  name: string;
  parts: Record<string, number | null>;
}

export function ShareBoard({
  totalLabel,
  countLabel,
  parts,
  rows,
  note,
  formatValue,
  onPickRow,
  layout = "donut",
}: {
  totalLabel: string;
  countLabel: string;
  parts: SharePart[];
  rows: ShareRow[];
  note?: string;
  formatValue?: (val: number) => string;
  onPickRow?: (key: string) => void;
  /** bar는 고리 없이 표의 막대만 보여 준다. */
  layout?: "donut" | "bar";
}) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const totals = rows.map((row) => partSum(row.parts));
  const sum = totals.reduce((acc, value) => acc + value, 0);
  const max = Math.max(1, ...totals);
  const gradient = ringGradient(
    rows.map((row, index) => ({
      color: SLICE[index % SLICE.length] ?? SLICE[0] ?? "transparent",
      share: sum === 0 ? 0 : (totals[index] ?? 0) / sum,
    })),
  );
  const formatted = formatValue ? formatValue(sum) : String(sum);
  const centerPx = shareCenterPx(formatted);
  const centerScale = shareCenterScale(formatted);
  const centerType = CENTER_TYPE[centerPx];

  return (
    <div className={styles.board}>
      <div className={layout === "bar" ? styles.shareBar : styles.share}>
        {layout === "bar" ? null : (
        <div className={styles.ring} style={{ background: gradient }} role="img" aria-label={`${totalLabel} ${formatted}, ${countLabel}`}>
          <div className={styles.hole}>
            <Caption1 className={mergeClasses(styles.quiet, styles.centerLine)}>{totalLabel}</Caption1>
            <EmphasisValue
              className={styles.total}
              style={{
                fontSize: centerType.fontSize,
                lineHeight: centerType.lineHeight,
                transform: centerScale < 1 ? `scale(${centerScale})` : undefined,
              }}
            >
              {formatted}
            </EmphasisValue>
            <Caption1 className={mergeClasses(styles.quiet, styles.centerLine)}>{countLabel}</Caption1>
          </div>
        </div>
        )}
        <div className={styles.frame}>
          <table className={mergeClasses(table.table, styles.shareTable)} aria-label="비중">
            <thead>
              <tr>
                <th className={mergeClasses(table.th, styles.head)} scope="col">
                  이름
                </th>
                <th className={mergeClasses(table.th, styles.head)} scope="col">
                  <span className={styles.legend}>
                    {parts.map((part, index) => (
                      <span key={part.key} className={styles.legendItem}>
                        <span className={styles.legendDot} style={{ backgroundColor: LEVEL[Math.min(index + 2, LEVEL.length - 1)] }} />
                        {part.label}
                      </span>
                    ))}
                  </span>
                </th>
                {parts.map((part) => (
                  <th key={part.key} className={mergeClasses(table.th, table.num, styles.head)} scope="col">
                    {part.label}
                  </th>
                ))}
                <th className={mergeClasses(table.th, table.num, styles.head)} scope="col">
                  합계
                </th>
                <th className={mergeClasses(table.th, table.num, styles.head)} scope="col">
                  비중
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const total = totals[index] ?? 0;
                const share = sum === 0 ? 0 : Math.round((total / sum) * 100);
                return (
                  <tr
                    key={row.key}
                    className={onPickRow ? styles.upcomingRowClickable : undefined}
                    onClick={onPickRow ? () => onPickRow(row.key) : undefined}
                  >
                    <th className={table.td} scope="row">
                      <span className={styles.name}>
                        <span className={styles.nameDot} style={{ backgroundColor: SLICE[index % SLICE.length] }} />
                        <Tooltip content={row.name} relationship="description">
                          <span className={styles.clip}>{row.name}</span>
                        </Tooltip>
                      </span>
                    </th>
                    <td className={table.td}>
                      <span className={styles.stack} style={{ width: `${Math.max(8, Math.round((total / max) * 100))}%` }} aria-hidden>
                        {parts.map((part, partIndex) => {
                          const value = row.parts[part.key] ?? 0;
                          if (!value || total === 0) return null;
                          return <span key={part.key} className={styles.stackPart} style={{ width: `${(value / total) * 100}%`, backgroundColor: LEVEL[Math.min(partIndex + 2, LEVEL.length - 1)] }} />;
                        })}
                      </span>
                    </td>
                    {parts.map((part) => (
                      <td key={part.key} className={mergeClasses(table.td, table.num)}>
                        {row.parts[part.key] == null ? <span className={styles.quiet}>—</span> : (formatValue ? formatValue(row.parts[part.key]!) : row.parts[part.key])}
                      </td>
                    ))}
                    <td className={mergeClasses(table.td, table.num)}>{formatValue ? formatValue(total) : total}</td>
                    <td className={mergeClasses(table.td, table.num)}>{share}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {note ? <Caption1 className={styles.note}>{note}</Caption1> : null}
    </div>
  );
}

export interface UpcomingItem {
  key: string;
  day: string;
  month: string;
  title: string;
  when: string;
  sub?: string;
  onClick?: () => void;
}

export function UpcomingList({ meta, items }: { meta?: string; items: UpcomingItem[] }) {
  const styles = useStyles();
  return (
    <div className={styles.board}>
      {meta ? (
        <div className={styles.boardHead}>
          <span />
          <Caption1 className={styles.quiet}>{meta}</Caption1>
        </div>
      ) : null}
      <ul className={styles.upcoming}>
        {items.map((item) => (
          <li
            key={item.key}
            className={mergeClasses(styles.upcomingRow, item.onClick && styles.upcomingRowClickable)}
            onClick={item.onClick}
            role={item.onClick ? "button" : undefined}
            tabIndex={item.onClick ? 0 : undefined}
            onKeyDown={
              item.onClick
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      item.onClick?.();
                    }
                  }
                : undefined
            }
          >
            <span className={styles.dateBadge}>
              <span className={styles.dateDay}>{item.day}</span>
              <Caption1 className={mergeClasses(styles.quiet, styles.dateMonth)}>{item.month}</Caption1>
            </span>
            <span className={styles.upcomingText}>
              <Tooltip content={item.title} relationship="description">
                <span className={styles.clip}>{item.title}</span>
              </Tooltip>
              <Caption1 className={styles.quiet}>
                {item.sub ? `${item.sub} · ${item.when}` : item.when}
              </Caption1>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface RhythmLane {
  key: string;
  name: string;
  values: number[];
  delayed?: number[];
}

export interface RhythmHeatCell {
  level: number;
  marked?: boolean;
  label: string;
}

export interface RhythmHeatRow {
  key: string;
  label: string;
  cells: RhythmHeatCell[];
}

export function RhythmBoard({
  rangeLabel,
  hint,
  columns,
  todayIndex,
  lanes,
  busiest,
  busiestNote,
  heatColumns,
  heatRows,
  dayLabels,
  weekday,
  weekend,
}: {
  rangeLabel: string;
  hint: string;
  columns: string[];
  todayIndex: number;
  lanes: RhythmLane[];
  busiest: string;
  busiestNote: string;
  heatColumns: string[];
  heatRows: RhythmHeatRow[];
  dayLabels: string[];
  weekday: number[];
  weekend: number[];
}) {
  const styles = useStyles();
  const plotRef = useRef<HTMLDivElement>(null);
  const drag = useRef<number | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [range, setRange] = useState<[number, number]>([0, Math.max(0, columns.length - 1)]);
  const count = columns.length;

  const columnAt = (event: PointerEvent<HTMLDivElement>) => {
    const rect = plotRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || count === 0) return 0;
    const ratio = (event.clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(count - 1, Math.floor(ratio * count)));
  };

  const onDown = (event: PointerEvent<HTMLDivElement>) => {
    const index = columnAt(event);
    drag.current = index;
    setRange([index, index]);
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onMove = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current == null) return;
    const index = columnAt(event);
    setRange([Math.min(drag.current, index), Math.max(drag.current, index)]);
  };
  const onUp = () => {
    drag.current = null;
  };

  const toggle = (key: string) => {
    setPicked((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));
  };

  return (
    <div className={styles.board}>
      <div className={styles.rhythm}>
        <div className={styles.rhythmMain}>
          <div className={styles.boardHead}>
            <div>
              <p className={styles.headline}>
                {count > 0 && (range[0] !== 0 || range[1] !== count - 1) ? `${columns[range[0]]} ~ ${columns[range[1]]}` : rangeLabel}
              </p>
              <Caption1 className={styles.quiet}>{hint}</Caption1>
            </div>
          </div>
          <div className={styles.laneHead}>
            <span />
            <div className={styles.columnLabels} style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
              {columns.map((column, index) => (
                <Caption1 key={column} className={mergeClasses(styles.axis, index === todayIndex && styles.axisToday)}>
                  {column}
                </Caption1>
              ))}
            </div>
          </div>
          <div className={styles.laneBody}>
            <div className={styles.laneNames}>
              {lanes.map((lane) => {
                const on = picked.length === 0 || picked.includes(lane.key);
                return (
                  <button key={lane.key} type="button" className={mergeClasses(styles.laneName, !on && styles.dim)} aria-pressed={picked.includes(lane.key)} onClick={() => toggle(lane.key)}>
                    <span className={styles.clip}>{lane.name}</span>
                  </button>
                );
              })}
            </div>
            <div ref={plotRef} className={styles.plot} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
              <span
                className={styles.wash}
                style={{
                  left: `${(range[0] / count) * 100}%`,
                  width: `${((range[1] - range[0] + 1) / count) * 100}%`,
                }}
              />
              {todayIndex >= 0 ? <span className={styles.today} style={{ left: `${((todayIndex + 0.5) / count) * 100}%` }} /> : null}
              {lanes.map((lane) => {
                const on = picked.length === 0 || picked.includes(lane.key);
                return <LaneArea key={lane.key} values={lane.values} delayed={lane.delayed ?? []} dim={!on} />;
              })}
            </div>
          </div>
          <Caption1 className={styles.note}>이름을 누르면 그 줄만 남습니다. 칸을 드래그하면 범위가 바뀝니다.</Caption1>
        </div>
        <div className={styles.rhythmSide}>
          <p className={styles.sideTitle}>{busiest}</p>
          <Caption1 className={styles.quiet}>{busiestNote}</Caption1>
          <div className={styles.heat} role="grid" aria-label="요일별 시각">
            <div className={styles.heatRow} style={{ gridTemplateColumns: `28px repeat(${heatColumns.length}, minmax(0, 1fr))` }}>
              <span />
              {heatColumns.map((column) => (
                <Caption1 key={column} className={styles.axis} role="columnheader">
                  {column}
                </Caption1>
              ))}
            </div>
            {heatRows.map((row) => (
              <span key={row.key} className={styles.heatRow} style={{ gridTemplateColumns: `28px repeat(${heatColumns.length}, minmax(0, 1fr))` }}>
                <Caption1 className={styles.heatLabel} role="rowheader">
                  {row.label}
                </Caption1>
                {row.cells.map((cell, index) => (
                  <span key={`${row.key}-${heatColumns[index] ?? index}`} className={styles.heatCell} style={{ backgroundColor: LEVEL[Math.max(0, Math.min(LEVEL.length - 1, cell.level))] }} role="gridcell" aria-label={cell.label}>
                    {cell.marked ? <span className={styles.heatMark} /> : null}
                  </span>
                ))}
              </span>
            ))}
          </div>
          <DayCurve labels={dayLabels} weekday={weekday} weekend={weekend} />
          <Caption1 className={styles.note}>회색이 진할수록 그 시각에 많습니다. 점은 지연입니다.</Caption1>
        </div>
      </div>
    </div>
  );
}

export interface ForecastDay {
  label: string;
  value: number;
  over?: boolean;
}

export interface ForecastDetail {
  used: number[];
  current: number[];
  even: number[];
  nowIndex: number;
  /** 막히는 지점. 없으면 점을 찍지 않는다. */
  runsOutIndex: number | null;
  resetLabel: string;
  caption: string;
  pace: string;
  runsOut: string;
  locked: string;
  paceNote: string;
  allowanceNote: string;
  days: ForecastDay[];
  allowance: number;
}

export interface ForecastRow {
  key: string;
  name: string;
  levels: number[];
  runsOut?: number;
  reset?: number;
  aside: string;
  late?: boolean;
  /** 표의 사용 비율. 0–100 */
  usedPct?: number;
  /** 고른 속도 눈금 위치. 0–100 */
  evenPct?: number;
  paceText?: string;
  paceFast?: boolean;
  resetIn?: string;
  detail?: ForecastDetail;
}

export function ForecastBoard({
  headline,
  sub,
  days,
  rows,
}: {
  headline: string;
  sub: string;
  days: string[];
  rows: ForecastRow[];
}) {
  const styles = useStyles();
  const first = rows.find((row) => row.detail)?.key ?? rows[0]?.key ?? "";
  const [selected, setSelected] = useState(first);
  const [view, setView] = useState<"lanes" | "table">("lanes");
  const [lateFirst, setLateFirst] = useState(true);
  const current = rows.find((row) => row.key === selected) ?? rows[0];
  const ordered = lateFirst ? [...rows].sort((a, b) => Number(Boolean(b.late)) - Number(Boolean(a.late)) || (b.usedPct ?? 0) - (a.usedPct ?? 0)) : rows;

  return (
    <div className={styles.board}>
      <div className={styles.boardHead}>
        <div>
          <p className={styles.headline}>{headline}</p>
          <Caption1 className={styles.quiet}>{sub}</Caption1>
        </div>
        <InkSwitch
          label="전망 보기"
          value={view}
          options={[
            { value: "lanes", label: "흐름" },
            { value: "table", label: "표" },
          ]}
          onChange={setView}
        />
      </div>
      {view === "table" ? (
        <div className={styles.tableMode}>
          <ForecastTable rows={ordered} selected={current?.key} lateFirst={lateFirst} onSort={() => setLateFirst((value) => !value)} onSelect={(key) => setSelected(key)} />
          {current?.detail ? (
            <div className={styles.forecastSplit}>
              <ForecastDetailView name={current.name} detail={current.detail} chartOnly />
              <ForecastDetailView name={current.name} detail={current.detail} statsOnly />
            </div>
          ) : null}
        </div>
      ) : (
      <div className={styles.forecast}>
        <div>
          <div className={styles.dayHead}>
            <span />
            <div className={styles.cells} style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
              {days.map((day) => (
                <Caption1 key={day} className={styles.axis}>
                  {day}
                </Caption1>
              ))}
            </div>
            <span />
          </div>
          <div role="listbox" aria-label="리셋 전 전망">
            {rows.map((row) => (
              <button
                key={row.key}
                type="button"
                className={mergeClasses(styles.forecastRow, current?.key === row.key && styles.forecastOn)}
                role="option"
                aria-selected={current?.key === row.key}
                aria-disabled={row.detail ? undefined : true}
                onClick={() => row.detail && setSelected(row.key)}
              >
                <span className={styles.forecastName}>{row.name}</span>
                <span className={styles.cells} style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
                  {row.levels.map((level, index) => (
                    <ForecastCell key={`${row.key}-${days[index] ?? index}`} level={level} runsOut={row.runsOut === index} locked={row.runsOut != null && row.reset != null && index > row.runsOut && index < row.reset} reset={row.reset === index} />
                  ))}
                </span>
                <span className={mergeClasses(styles.aside, row.late && styles.late)}>{row.aside}</span>
              </button>
            ))}
          </div>
          <Caption1 className={styles.note}>회색은 예상 소진입니다. 점은 막힘, 빗금은 그 뒤 잠긴 날, 막대는 리셋입니다.</Caption1>
        </div>
        {current?.detail ? <ForecastDetailView name={current.name} detail={current.detail} /> : null}
      </div>
      )}
    </div>
  );
}

function ForecastTable({
  rows,
  selected,
  lateFirst,
  onSort,
  onSelect,
}: {
  rows: ForecastRow[];
  selected?: string;
  lateFirst: boolean;
  onSort: () => void;
  onSelect: (key: string) => void;
}) {
  const styles = useStyles();
  const table = useDataTableStyles();
  return (
    <div className={styles.frame}>
      <table className={mergeClasses(table.table, styles.shareTable)} aria-label="리셋 전 전망 표">
        <thead>
          <tr>
            <th className={mergeClasses(table.th, styles.head)} scope="col">
              이름
            </th>
            <th className={mergeClasses(table.th, styles.head)} scope="col">
              사용
            </th>
            <th className={mergeClasses(table.th, table.num, styles.head)} scope="col">
              속도
            </th>
            <th className={mergeClasses(table.th, styles.head)} scope="col">
              <button type="button" className={styles.sort} aria-pressed={lateFirst} onClick={onSort}>
                막힘 {lateFirst ? "↓" : "↑"}
              </button>
            </th>
            <th className={mergeClasses(table.th, table.num, styles.head)} scope="col">
              리셋
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.key}
              className={mergeClasses(row.detail && styles.rowPick, selected === row.key && styles.rowOn)}
              tabIndex={row.detail ? 0 : undefined}
              aria-selected={selected === row.key}
              onClick={() => row.detail && onSelect(row.key)}
              onKeyDown={(event) => {
                if (!row.detail || (event.key !== "Enter" && event.key !== " ")) return;
                event.preventDefault();
                onSelect(row.key);
              }}
            >
              <th className={table.td} scope="row">
                {row.name}
              </th>
              <td className={table.td}>
                <span className={styles.used}>
                  <span className={styles.usedPct}>{row.usedPct == null ? "—" : `${row.usedPct}%`}</span>
                  <span className={styles.meter} aria-hidden>
                    <span className={styles.meterFill} style={{ width: `${row.usedPct ?? 0}%` }} />
                    {row.evenPct != null ? <span className={styles.meterTick} style={{ left: `${row.evenPct}%` }} /> : null}
                  </span>
                </span>
              </td>
              <td className={mergeClasses(table.td, table.num, row.paceFast && styles.late)}>{row.paceText ?? "—"}</td>
              <td className={mergeClasses(table.td, row.late && styles.late)}>
                {row.late ? <span className={styles.lateDot} /> : null}
                {row.aside}
              </td>
              <td className={mergeClasses(table.td, table.num)}>{row.resetIn ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Caption1 className={styles.note}>점은 리셋 전에 막히는 줄입니다. 눈금은 고른 속도이고, 막힘 머리글을 누르면 순서가 바뀝니다.</Caption1>
    </div>
  );
}

function InkSwitch<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  label: string;
}) {
  const styles = useStyles();
  return (
    <div role="tablist" aria-label={label} className={styles.switch}>
      {options.map((option) => (
        <button key={option.value} type="button" role="tab" aria-selected={option.value === value} className={mergeClasses(styles.switchItem, option.value === value && styles.switchOn)} onClick={() => onChange(option.value)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ForecastCell({ level, runsOut, locked, reset }: { level: number; runsOut: boolean; locked: boolean; reset: boolean }) {
  const styles = useStyles();
  if (reset) return <span className={styles.reset}>리셋</span>;
  if (locked) return <span className={mergeClasses(styles.cell, styles.locked)} />;
  if (level < 0) return <span className={mergeClasses(styles.cell, styles.emptyCell)} />;
  return (
    <span className={mergeClasses(styles.cell, runsOut && styles.out)} style={runsOut ? undefined : { backgroundColor: LEVEL[Math.max(0, Math.min(LEVEL.length - 1, level))] }}>
      {runsOut ? <span className={styles.outDot} /> : null}
    </span>
  );
}

function ForecastDetailView({ name, detail, chartOnly, statsOnly }: { name: string; detail: ForecastDetail; chartOnly?: boolean; statsOnly?: boolean }) {
  const styles = useStyles();
  const label = useId();
  const max = Math.max(detail.allowance, ...detail.days.map((day) => day.value), 1);
  return (
    <div className={styles.detail}>
      {statsOnly ? null : (
        <>
          <p className={styles.sideTitle}>{name}</p>
          <Caption1 className={styles.quiet}>{detail.caption}</Caption1>
          <ForecastLine detail={detail} patternId={label} />
        </>
      )}
      {chartOnly ? null : (
        <>
      <div className={styles.stats}>
        <Stat label="속도" value={detail.pace} />
        <Stat label="막힘" value={detail.runsOut} />
        <Stat label="잠김" value={detail.locked} />
      </div>
      <Caption1 className={styles.quiet}>{detail.paceNote}</Caption1>
      <Caption1 className={styles.quiet}>{detail.allowanceNote}</Caption1>
      <div className={styles.pace} aria-hidden>
        <span className={styles.allow} style={{ bottom: `${(detail.allowance / max) * 100}%` }} />
        {detail.days.map((day) => (
          <span key={day.label} className={styles.paceCol}>
            <span className={mergeClasses(styles.paceBar, day.over && styles.paceOver)} style={{ height: `${(day.value / max) * 100}%` }} />
            <Caption1 className={styles.axis}>{day.label}</Caption1>
          </span>
        ))}
      </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  const styles = useStyles();
  return (
    <span className={styles.stat}>
      <Caption1 className={styles.quiet}>{label}</Caption1>
      <span className={styles.statValue}>{value}</span>
    </span>
  );
}

function ForecastLine({ detail, patternId }: { detail: ForecastDetail; patternId: string }) {
  const styles = useStyles();
  const width = 240;
  const height = 88;
  const pad = 8;
  const x = (index: number, count: number) => (count <= 1 ? pad : pad + (index / (count - 1)) * (width - pad * 2));
  const y = (value: number) => pad + (1 - Math.max(0, Math.min(100, value)) / 100) * (height - pad * 2);
  const line = (values: number[]) => values.map((value, index) => `${x(index, values.length)},${y(value)}`).join(" ");
  const outX = detail.runsOutIndex == null ? null : x(detail.runsOutIndex, detail.used.length);
  return (
    <svg className={styles.line} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${detail.caption}. ${detail.resetLabel}`}>
      <pattern id={patternId} width="6" height="6" patternUnits="userSpaceOnUse">
        <path d="M0 6 L6 0" stroke={tokens.colorPaletteRedForeground1} strokeWidth="1" />
      </pattern>
      {outX != null ? <rect x={outX} y={pad} width={Math.max(0, width - pad - outX)} height={height - pad * 2} fill={`url(#${patternId})`} opacity="0.45" /> : null}
      <polyline points={line(detail.even)} fill="none" stroke={tokens.colorNeutralStroke1} strokeDasharray="2 3" />
      <polyline points={line(detail.current)} fill="none" stroke={tokens.colorNeutralForeground4} strokeDasharray="4 3" />
      <polyline points={line(detail.used)} fill="none" stroke={tokens.colorNeutralForeground1} />
      <line x1={x(detail.nowIndex, detail.used.length)} x2={x(detail.nowIndex, detail.used.length)} y1={pad} y2={height - pad} stroke={tokens.colorNeutralStrokeAccessible} strokeDasharray="2 2" />
      {outX != null && detail.runsOutIndex != null ? <circle cx={outX} cy={y(detail.used[detail.runsOutIndex] ?? 100)} r="3" fill={tokens.colorPaletteRedForeground1} /> : null}
    </svg>
  );
}

function LaneArea({ values, delayed, dim }: { values: number[]; delayed: number[]; dim: boolean }) {
  const styles = useStyles();
  const width = Math.max(values.length, 1) * 12;
  const height = 32;
  const x = (index: number) => ((index + 0.5) / Math.max(values.length, 1)) * width;
  const y = (value: number) => height - (Math.max(0, Math.min(100, value)) / 100) * (height - 4);
  const path = values.length
    ? `M${x(0)},${height} ${values.map((value, index) => `L${x(index)},${y(value)}`).join(" ")} L${x(values.length - 1)},${height} Z`
    : "";
  return (
    <svg className={mergeClasses(styles.lane, dim && styles.dim)} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden>
      <path d={path} fill={tokens.colorNeutralForeground4} opacity="0.45" />
      <polyline points={values.map((value, index) => `${x(index)},${y(value)}`).join(" ")} fill="none" stroke={tokens.colorNeutralForeground3} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      {delayed.map((index) => (
        <rect key={index} x={x(index) - 1} y={y(values[index] ?? 0) - 4} width="2" height="6" fill={tokens.colorPaletteDarkOrangeForeground1} />
      ))}
    </svg>
  );
}

function DayCurve({ labels, weekday, weekend }: { labels: string[]; weekday: number[]; weekend: number[] }) {
  const styles = useStyles();
  const width = 220;
  const height = 64;
  const line = (values: number[]) => {
    const max = Math.max(1, ...weekday, ...weekend);
    return values
      .map((value, index) => {
        const x = values.length <= 1 ? 0 : (index / (values.length - 1)) * width;
        const y = height - 12 - (value / max) * (height - 16);
        return `${x},${y}`;
      })
      .join(" ");
  };
  return (
    <div>
      <svg className={styles.dayCurve} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="하루 분포">
        <polyline points={line(weekend)} fill="none" stroke={tokens.colorNeutralStroke1} strokeDasharray="3 3" />
        <polyline points={line(weekday)} fill="none" stroke={tokens.colorNeutralForeground2} />
      </svg>
      <div className={styles.dayLabels}>
        {labels.map((label) => (
          <Caption1 key={label} className={styles.axis}>
            {label}
          </Caption1>
        ))}
      </div>
    </div>
  );
}

function partSum(parts: Record<string, number | null>): number {
  return Object.values(parts).reduce<number>((sum, value) => sum + (value ?? 0), 0);
}

function ringGradient(slices: { color: string; share: number }[]): string {
  let cursor = 0;
  const stops = slices
    .filter((slice) => slice.share > 0)
    .map((slice) => {
      const start = cursor;
      cursor += slice.share * 100;
      return `${slice.color} ${start}% ${cursor}%`;
    });
  if (stops.length === 0) return tokens.colorNeutralBackground4;
  return `conic-gradient(${stops.join(", ")})`;
}

const useStyles = makeStyles({
  board: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
  },
  headline: {
    margin: 0,
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  quiet: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
  },
  dateMonth: {
    whiteSpace: "nowrap",
  },
  note: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
  },
  late: { color: tokens.colorPaletteDarkOrangeForeground1 },
  share: {
    display: "grid",
    gridTemplateColumns: "188px minmax(0, 1fr)",
    gap: tokens.spacingHorizontalXL,
    alignItems: "center",
    "@media (max-width: 800px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  shareBar: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    alignItems: "center",
  },
  ring: {
    width: `${SHARE_RING}px`,
    height: `${SHARE_RING}px`,
    borderRadius: tokens.borderRadiusCircular,
    display: "grid",
    placeItems: "center",
    justifySelf: "center",
  },
  hole: {
    width: `${SHARE_HOLE}px`,
    height: `${SHARE_HOLE}px`,
    boxSizing: "border-box",
    paddingLeft: "6px",
    paddingRight: "6px",
    overflow: "hidden",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "2px",
  },
  centerLine: {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "center",
  },
  total: {
    maxWidth: "100%",
    minWidth: 0,
    whiteSpace: "nowrap",
    textAlign: "center",
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground1,
  },
  frame: {
    minWidth: 0,
    overflowX: "auto",
  },
  shareTable: {
    minWidth: "640px",
  },
  head: {
    position: "static",
    top: "auto",
    zIndex: 0,
  },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: 0,
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontWeight: tokens.fontWeightRegular,
  },
  legendDot: {
    width: "8px",
    height: "8px",
    borderRadius: tokens.borderRadiusCircular,
  },
  name: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    textAlign: "left",
    fontWeight: tokens.fontWeightRegular,
    maxWidth: "160px",
  },
  nameDot: {
    width: "8px",
    height: "8px",
    borderRadius: tokens.borderRadiusCircular,
    flexShrink: 0,
  },
  clip: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  stack: {
    display: "flex",
    height: "8px",
    minWidth: "8px",
    borderRadius: tokens.borderRadiusCircular,
    overflow: "hidden",
    backgroundColor: tokens.colorNeutralBackground4,
  },
  stackPart: {
    height: "100%",
  },
  upcoming: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
  },
  upcomingRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
    minHeight: "44px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ":last-child": { borderBottom: "none" },
  },
  upcomingRowClickable: {
    cursor: "pointer",
    paddingLeft: "4px",
    paddingRight: "4px",
    borderRadius: tokens.borderRadiusMedium,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  dateBadge: {
    width: "48px",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: "4px",
    paddingBottom: "4px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  dateDay: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground1,
  },
  upcomingText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    gap: "1px",
  },
  rhythm: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.4fr) minmax(220px, 0.8fr)",
    gap: tokens.spacingHorizontalXL,
    "@media (max-width: 800px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  rhythmMain: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  rhythmSide: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  sideTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  laneHead: {
    display: "grid",
    gridTemplateColumns: "120px minmax(0, 1fr)",
    gap: "8px",
  },
  columnLabels: {
    display: "grid",
    gap: "3px",
    minWidth: 0,
  },
  laneBody: {
    display: "grid",
    gridTemplateColumns: "120px minmax(0, 1fr)",
    gap: "8px",
    minWidth: 0,
  },
  laneNames: {
    display: "flex",
    flexDirection: "column",
  },
  laneName: {
    height: "32px",
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    padding: 0,
    border: "none",
    background: "none",
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    textAlign: "left",
    cursor: "pointer",
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  plot: {
    position: "relative",
    minWidth: 0,
    cursor: "crosshair",
    touchAction: "none",
  },
  wash: {
    position: "absolute",
    top: 0,
    bottom: 0,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  today: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: "1px",
    backgroundColor: tokens.colorNeutralStrokeAccessible,
  },
  lane: {
    display: "block",
    width: "100%",
    height: "32px",
    position: "relative",
    zIndex: 1,
  },
  dim: { opacity: 0.35 },
  axis: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  axisToday: { color: tokens.colorNeutralForeground1 },
  heat: {
    display: "flex",
    flexDirection: "column",
    gap: "3px",
  },
  heatRow: {
    display: "grid",
    gridTemplateColumns: "28px repeat(4, minmax(0, 1fr))",
    gap: "3px",
    alignItems: "center",
  },
  heatLabel: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
  },
  heatCell: {
    position: "relative",
    height: "16px",
    borderRadius: tokens.borderRadiusSmall,
  },
  heatMark: {
    position: "absolute",
    left: "50%",
    top: "50%",
    width: "5px",
    height: "5px",
    marginLeft: "-2.5px",
    marginTop: "-2.5px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorPaletteDarkOrangeForeground1,
  },
  dayCurve: {
    width: "100%",
    height: "64px",
  },
  dayLabels: {
    display: "flex",
    justifyContent: "space-between",
  },
  forecast: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.3fr) minmax(220px, 0.8fr)",
    gap: tokens.spacingHorizontalXL,
    "@media (max-width: 800px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  dayHead: {
    display: "grid",
    gridTemplateColumns: "112px minmax(0, 1fr) 148px",
    gap: "8px",
    marginTop: tokens.spacingVerticalS,
  },
  forecastRow: {
    display: "grid",
    gridTemplateColumns: "112px minmax(0, 1fr) 148px",
    gap: "8px",
    alignItems: "center",
    width: "100%",
    minHeight: "32px",
    padding: "2px 4px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    background: "none",
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    textAlign: "left",
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
    "&[aria-disabled='true']": {
      cursor: "default",
    },
  },
  forecastOn: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  forecastName: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cells: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
    gap: "3px",
    minWidth: 0,
  },
  cell: {
    height: "18px",
    borderRadius: "2px",
    position: "relative",
  },
  emptyCell: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
  },
  out: {
    backgroundColor: tokens.colorPaletteDarkOrangeBackground3,
  },
  outDot: {
    position: "absolute",
    left: "50%",
    top: "50%",
    width: "6px",
    height: "6px",
    marginLeft: "-3px",
    marginTop: "-3px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorPaletteRedForeground1,
  },
  locked: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    backgroundImage: `repeating-linear-gradient(-45deg, transparent, transparent 2px, ${tokens.colorPaletteRedBackground3} 2px, ${tokens.colorPaletteRedBackground3} 3px)`,
  },
  reset: {
    height: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
    fontWeight: tokens.fontWeightRegular,
  },
  aside: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  detail: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  line: {
    width: "100%",
    height: "88px",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalS,
  },
  stat: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
  statValue: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    whiteSpace: "nowrap",
  },
  pace: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
    gap: "6px",
    height: "84px",
    alignItems: "end",
  },
  paceCol: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    alignItems: "center",
    height: "100%",
    gap: "2px",
    minWidth: 0,
  },
  paceBar: {
    width: "100%",
    maxWidth: "18px",
    borderRadius: "2px",
    backgroundColor: tokens.colorNeutralForeground4,
  },
  paceOver: {
    backgroundColor: tokens.colorPaletteDarkOrangeBackground3,
  },
  allow: {
    position: "absolute",
    left: 0,
    right: 0,
    height: "1px",
    borderTop: `1px dashed ${tokens.colorNeutralStrokeAccessible}`,
  },
  boardHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
  },
  switch: {
    display: "flex",
    height: "28px",
    padding: "2px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    flexShrink: 0,
  },
  switchItem: {
    border: "none",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    fontFamily: "inherit",
    padding: "0 10px",
    borderRadius: tokens.borderRadiusSmall,
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}` },
  },
  switchOn: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
  },
  tableMode: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  forecastSplit: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.2fr) minmax(220px, 0.8fr)",
    gap: "20px",
    alignItems: "start",
  },
  rowOn: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  rowPick: {
    cursor: "pointer",
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}` },
  },
  sort: {
    border: "none",
    backgroundColor: "transparent",
    color: "inherit",
    font: "inherit",
    padding: 0,
    cursor: "pointer",
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}` },
  },
  used: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: "148px",
  },
  usedPct: {
    width: "36px",
    flexShrink: 0,
    fontVariantNumeric: "tabular-nums",
  },
  meter: {
    position: "relative",
    flex: 1,
    height: "6px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground5,
  },
  meterFill: {
    display: "block",
    height: "100%",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralForeground4,
  },
  meterTick: {
    position: "absolute",
    top: "-3px",
    width: "1px",
    height: "12px",
    backgroundColor: tokens.colorNeutralStrokeAccessible,
  },
  lateDot: {
    display: "inline-block",
    width: "6px",
    height: "6px",
    marginRight: "6px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorPaletteRedForeground1,
  },
});
